import type { ICellEditorParams, SuppressKeyboardEventParams } from 'ag-grid-community'
import { type SelectProps, Select } from 'antd'
import type { DefaultOptionType } from 'antd/lib/select'
import type React from 'react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export type SelectEditorParams = ICellEditorParams &
  SelectProps & {
    accessor?: string
    linkedField?: string
    width?: number
    setupFunc?: (setValue: any, linkedField: any) => void
    hideSearch?: boolean
    onChange?: (value: any, option: DefaultOptionType | DefaultOptionType[], data: any) => void
  }

// Utility function to convert string[] to DefaultOptionType[] that antd likes. Might be useful outside of this component.
export function toAntdOption(option: SelectEditorParams['options'][number]): DefaultOptionType {
  if (typeof option !== 'string') return option
  return {
    label: option,
    value: option,
  }
}

export const CustomSelectEditor: React.FC<SelectEditorParams> = forwardRef((props, ref) => {
  const primaryKey = props.colDef.field || props.colDef.colId
  const [value, setValue] = useState(props?.data?.[props.accessor || primaryKey] || [])
  const [hasValueChanged, setHasValueChanged] = useState(false)
  const refInput = useRef(null)

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })

  const handleSelect = (value: any, option: DefaultOptionType) => {
    setValue(value)
    setHasValueChanged(true)
  }

  useEffect(() => {
    props.setupFunc && props.setupFunc(setValue, props.data?.[props.linkedField])
  }, [])

  useEffect(() => {
    if (hasValueChanged) {
      if (!props.mode) {
        props?.api?.stopEditing()
        // @ts-ignore - colId is a private field but we're using it anyway
        props?.api?.setFocusedCell(props?.rowIndex, props?.column?.colId)
      }
    }
  }, [hasValueChanged])

  return (
    <Select
      showSearch={!props?.hideSearch}
      style={{ minWidth: props.width || 300, maxWidth: props.width || 400, margin: 0 }}
      value={value}
      onChange={(value, option) => {
        setValue(value)
        typeof props.onChange === 'function' && props.onChange(value, option, props.data)
      }}
      mode={props.mode}
      allowClear
      defaultOpen
      ref={refInput}
      autoFocus
      menuItemSelectedIcon={null}
      options={props.options || []}
      placeholder={props.placeholder || 'Select'}
      filterOption={props.filterOption || null}
      onClear={() => setValue('')}
      optionLabelProp='label'
    >
      {props.options?.map((item) => (
        <Select.Option key={item.value} className={item.className} value={item.value}>
          {item.label}
        </Select.Option>
      ))}
    </Select>
  )
})

// Utility function to spread the necessary props for ag-grid on a column definition
export function useSelectCellEditor(cellEditorParams: SelectEditorParams) {
  return {
    cellEditor: CustomSelectEditor,
    cellEditorPopup: cellEditorParams.isPopup ?? true,
    cellEditorParams: {
      ...cellEditorParams,
      options: (cellEditorParams.options ?? []).map(toAntdOption),
    },
    suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
      const { key } = params.event
      return params.editing && key === 'Enter'
    },
  }
}
